/*
 * Lemieux et Cie — Site-wide Wishlist
 * ====================================
 * A localStorage-backed wishlist used for:
 *   1. PDP / product-card heart buttons (toggle save state per variant)
 *   2. The mini-cart drawer's "Save for later" flow
 *   3. The /pages/wishlist viewer
 *
 * Public API:
 *   window.LemieuxWishlist.get()                          -> array of saved entries
 *   window.LemieuxWishlist.has(variantId)                 -> boolean
 *   window.LemieuxWishlist.add(variantId, productHandle)  -> void
 *   window.LemieuxWishlist.remove(variantId)              -> void
 *   window.LemieuxWishlist.toggle(variantId, handle)      -> void
 *   window.LemieuxWishlist.count()                        -> number
 *   window.LemieuxWishlist.saveForLater(itemKey, variantId, handle)
 *                                                          -> remove from cart + add to wishlist
 *   window.LemieuxWishlist.moveToBag(variantId)           -> add to cart + remove from wishlist
 *   window.LemieuxWishlist.renderInto(containerEl, opts)  -> hydrate saved items
 *
 * Events (on window):
 *   'wishlist:changed' — fires after any add/remove; detail.items = current array
 *
 * Storage shape (in localStorage under `lemieux_saved_for_later`):
 *   [{ variantId: 41234, productHandle: 'pernelle-stool', savedAt: 1747318800000 }]
 */
(function () {
  if (window.LemieuxWishlist) return;

  var STORAGE_KEY = 'lemieux_saved_for_later';
  var EVENT = 'wishlist:changed';

  function readStorage() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeStorage(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('wishlist write failed', e);
    }
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { items: items } }));
  }

  function idsEqual(a, b) {
    return String(a) === String(b);
  }

  var Wishlist = {
    STORAGE_KEY: STORAGE_KEY,
    EVENT: EVENT,
    get: function () { return readStorage(); },
    has: function (variantId) {
      return readStorage().some(function (i) { return idsEqual(i.variantId, variantId); });
    },
    count: function () { return readStorage().length; },
    add: function (variantId, productHandle) {
      var items = readStorage();
      if (items.some(function (i) { return idsEqual(i.variantId, variantId); })) return;
      items.push({
        variantId: Number(variantId),
        productHandle: productHandle,
        savedAt: Date.now()
      });
      writeStorage(items);
    },
    remove: function (variantId) {
      var items = readStorage().filter(function (i) { return !idsEqual(i.variantId, variantId); });
      writeStorage(items);
    },
    toggle: function (variantId, productHandle) {
      if (this.has(variantId)) this.remove(variantId);
      else this.add(variantId, productHandle);
    },
    clear: function () { writeStorage([]); },

    /* Remove a line from the live cart and stash its variant in the wishlist. */
    saveForLater: function (itemKey, variantId, productHandle) {
      var self = this;
      var body = new FormData();
      body.append('id', itemKey);
      body.append('quantity', 0);
      return fetch(window.Shopify.routes.root + 'cart/change.js', { method: 'POST', body: body })
        .then(function (r) { return r.json(); })
        .then(function () {
          self.add(variantId, productHandle);
        })
        .catch(function (err) { console.error('saveForLater failed', err); });
    },

    /* Add a saved variant back to the cart and remove it from the wishlist. */
    moveToBag: function (variantId) {
      var self = this;
      var body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);
      return fetch(window.Shopify.routes.root + 'cart/add.js', { method: 'POST', body: body })
        .then(function (r) { return r.json(); })
        .then(function () {
          self.remove(variantId);
        })
        .catch(function (err) { console.error('moveToBag failed', err); });
    },

    /*
     * Hydrate a container with the user's saved items. The container chooses
     * its own item template via the `template` option. We fetch each product
     * via /products/{handle}.js, find the saved variant, and feed that to the
     * template. Items whose product/variant no longer exists are silently
     * dropped from the wishlist.
     */
    renderInto: function (container, opts) {
      opts = opts || {};
      var items = this.get();
      var template = opts.template;
      var onEmpty = opts.onEmpty || function (c) { c.innerHTML = ''; };
      var self = this;
      if (!items.length) {
        onEmpty(container);
        return Promise.resolve();
      }
      return Promise.all(items.map(function (saved) {
        return fetch('/products/' + saved.productHandle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (product) {
            if (!product) return null;
            var variant = (product.variants || []).find(function (v) {
              return idsEqual(v.id, saved.variantId);
            });
            return variant ? { saved: saved, product: product, variant: variant } : null;
          })
          .catch(function () { return null; });
      })).then(function (results) {
        var valid = results.filter(function (r) { return r; });
        // Prune stale wishlist entries (product or variant gone) silently.
        var validIds = valid.map(function (r) { return String(r.variant.id); });
        var prevIds = items.map(function (i) { return String(i.variantId); });
        var stale = prevIds.filter(function (id) { return validIds.indexOf(id) === -1; });
        if (stale.length) {
          var pruned = readStorage().filter(function (i) {
            return stale.indexOf(String(i.variantId)) === -1;
          });
          writeStorage(pruned);
        }
        if (!valid.length) {
          onEmpty(container);
          return;
        }
        container.innerHTML = valid.map(template).join('');
      });
    }
  };

  window.LemieuxWishlist = Wishlist;

  /* ------------------------------------------------------------
   * Heart-button hydration
   * ------------------------------------------------------------
   * Buttons rendered by snippets/wishlist-button.liquid carry a
   * data-wishlist-toggle attribute. We sync aria-pressed + .is-active
   * with the wishlist state and re-sync on every change event.
   */
  function hydrateButtons(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      if (btn.__wishlistHydrated) return;
      btn.__wishlistHydrated = true;
      var variantId = btn.dataset.variantId;
      var handle = btn.dataset.productHandle;

      function sync() {
        var on = Wishlist.has(btn.dataset.variantId || variantId);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-active', on);
      }
      sync();

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var vid = btn.dataset.variantId || variantId;
        var ph = btn.dataset.productHandle || handle;
        Wishlist.toggle(vid, ph);
      });

      window.addEventListener(EVENT, sync);
    });
  }

  /* ------------------------------------------------------------
   * Drawer integration
   * ------------------------------------------------------------
   * Delegated handlers for "Save for later", "Move to bag", "Remove"
   * inside the cart drawer.
   */
  document.addEventListener('click', function (e) {
    var saveBtn = e.target.closest('[data-wishlist-save]');
    if (saveBtn) {
      e.preventDefault();
      Wishlist.saveForLater(
        saveBtn.dataset.itemKey,
        saveBtn.dataset.variantId,
        saveBtn.dataset.productHandle
      );
      return;
    }

    var moveBtn = e.target.closest('[data-wishlist-move-to-bag]');
    if (moveBtn) {
      e.preventDefault();
      Wishlist.moveToBag(moveBtn.dataset.variantId);
      return;
    }

    var removeBtn = e.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      Wishlist.remove(removeBtn.dataset.variantId);
      return;
    }
  });

  /* ------------------------------------------------------------
   * Auto-render hooks
   * ------------------------------------------------------------
   * Any container marked [data-wishlist-render] gets populated with
   * the user's saved items using the inline template specified by
   * data-wishlist-template ("drawer" | "page").
   */
  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function priceWithCompare(variant, priceClass, compareClass) {
    var compareAt = variant.compare_at_price;
    var hasCompare = typeof compareAt === 'number' && compareAt > variant.price;
    return '<p class="' + priceClass + '">'
      +    '<span>' + formatMoney(variant.price) + '</span>'
      +    (hasCompare ? ' <s class="' + compareClass + '">' + formatMoney(compareAt) + '</s>' : '')
      + '</p>';
  }

  var TEMPLATES = {
    drawer: function (data) {
      var p = data.product, v = data.variant;
      var img = (v.featured_image && v.featured_image.src) || p.featured_image || '';
      var hasVariant = v.title && v.title !== 'Default Title';
      return ''
        + '<div class="mini-cart__saved-item" data-variant-id="' + v.id + '">'
        +   '<a href="/products/' + escapeHtml(p.handle) + '" class="mini-cart__saved-media" tabindex="-1" aria-hidden="true">'
        +     (img ? '<img src="' + escapeHtml(img) + '" alt="" loading="lazy">' : '')
        +   '</a>'
        +   '<div class="mini-cart__saved-body">'
        +     '<a href="/products/' + escapeHtml(p.handle) + '" class="mini-cart__saved-title">' + escapeHtml(p.title) + '</a>'
        +     (hasVariant ? '<p class="mini-cart__saved-variant">' + escapeHtml(v.title.replace(' / ', ' · ')) + '</p>' : '')
        +     priceWithCompare(v, 'mini-cart__saved-price', 'mini-cart__saved-compare')
        +     '<div class="mini-cart__saved-actions">'
        +       '<button type="button" class="mini-cart__saved-action" data-wishlist-move-to-bag data-variant-id="' + v.id + '">Move to bag</button>'
        +       '<span class="mini-cart__saved-sep" aria-hidden="true">·</span>'
        +       '<button type="button" class="mini-cart__saved-action" data-wishlist-remove data-variant-id="' + v.id + '">Remove</button>'
        +     '</div>'
        +   '</div>'
        + '</div>';
    },
    page: function (data) {
      var p = data.product, v = data.variant;
      var img = (v.featured_image && v.featured_image.src) || p.featured_image || '';
      var hasVariant = v.title && v.title !== 'Default Title';
      return ''
        + '<article class="wishlist-card" data-variant-id="' + v.id + '">'
        +   '<a href="/products/' + escapeHtml(p.handle) + '" class="wishlist-card__media">'
        +     (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '')
        +   '</a>'
        +   '<div class="wishlist-card__body">'
        +     '<a href="/products/' + escapeHtml(p.handle) + '" class="wishlist-card__title">' + escapeHtml(p.title) + '</a>'
        +     (hasVariant ? '<p class="wishlist-card__variant">' + escapeHtml(v.title.replace(' / ', ' · ')) + '</p>' : '')
        +     priceWithCompare(v, 'wishlist-card__price', 'wishlist-card__compare')
        +     '<div class="wishlist-card__actions">'
        +       '<button type="button" class="wishlist-card__primary" data-wishlist-move-to-bag data-variant-id="' + v.id + '">Move to bag</button>'
        +       '<button type="button" class="wishlist-card__secondary" data-wishlist-remove data-variant-id="' + v.id + '">Remove</button>'
        +     '</div>'
        +   '</div>'
        + '</article>';
    }
  };

  function renderTargets() {
    document.querySelectorAll('[data-wishlist-render]').forEach(function (el) {
      var kind = el.dataset.wishlistTemplate || 'drawer';
      var template = TEMPLATES[kind];
      if (!template) return;
      Wishlist.renderInto(el, {
        template: template,
        onEmpty: function (container) {
          if (el.dataset.wishlistEmpty) {
            container.innerHTML = el.dataset.wishlistEmpty;
          } else {
            container.innerHTML = '';
          }
          // Also let the section toggle visibility of surrounding chrome.
          el.dispatchEvent(new CustomEvent('wishlist:rendered-empty', { bubbles: true }));
        }
      }).then(function () {
        el.dispatchEvent(new CustomEvent('wishlist:rendered', { bubbles: true }));
      });
    });
  }

  function init() {
    hydrateButtons();
    renderTargets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-hydrate after any wishlist change and after cart drawer reload */
  window.addEventListener(EVENT, function () { renderTargets(); });
  window.addEventListener('pagination-loaded', function () { init(); });
})();
